import { useState, useCallback } from 'react';
import { 
  SatelliteGroup, 
  SatelliteRecord, 
  CloseApproachEvent, 
  CloseApproachConfig 
} from '../types/satellite';
import { CelestrakService } from '../services/celestrakService';
import { N2YOService } from '../services/n2yoService';
import { CollisionDetectionService } from '../services/collisionDetection';
import { DEFAULT_CONFIG } from '../config/satelliteGroups';

interface UseSatelliteDataReturn {
  satellites: SatelliteRecord[];
  loading: boolean;
  error: string | null;
  loadSatellites: (group: SatelliteGroup, maxSats?: number) => Promise<void>;
  clearError: () => void;
}

export function useSatelliteData(): UseSatelliteDataReturn {
  const [satellites, setSatellites] = useState<SatelliteRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const celestrakService = new CelestrakService();
  const n2yoService = new N2YOService();
  
  // Log N2YO availability for demo purposes
  if (n2yoService.isAvailable()) {
    console.log('✓ N2YO API integration active');
  }
  
  const loadSatellites = useCallback(async (
    group: SatelliteGroup, 
    maxSats: number = DEFAULT_CONFIG.maxSatellites
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const tleData = await celestrakService.fetchTLEData(group, maxSats);
      const satelliteRecords = celestrakService.createSatelliteRecords(tleData);
      setSatellites(satelliteRecords);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(message);
      setSatellites([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  return {
    satellites,
    loading,
    error,
    loadSatellites,
    clearError,
  };
}

interface UseCloseApproachReturn {
  events: CloseApproachEvent[];
  running: boolean;
  runAnalysis: (satellites: SatelliteRecord[], config: CloseApproachConfig) => Promise<void>;
  clearEvents: () => void;
}

export function useCloseApproach(): UseCloseApproachReturn {
  const [events, setEvents] = useState<CloseApproachEvent[]>([]);
  const [running, setRunning] = useState(false);
  
  const runAnalysis = useCallback(async (
    satellites: SatelliteRecord[], 
    config: CloseApproachConfig
  ) => {
    if (!satellites.length || running) return;
    
    setRunning(true);
    
    try {
      // Limit satellites for performance
      const subset = satellites.slice(0, Math.min(satellites.length, 1200));
      const detectedEvents = await CollisionDetectionService.detectCloseApproaches(subset, config);
      
      // Sort by criticality and filter duplicates
      const sortedEvents = CollisionDetectionService.sortEventsByCriticality(detectedEvents);
      const filteredEvents = CollisionDetectionService.filterByTimeSeparation(sortedEvents);
      
      setEvents(filteredEvents);
    } catch (error) {
      console.error('Close approach analysis failed:', error);
      setEvents([]);
    } finally {
      setRunning(false);
    }
  }, [running]);
  
  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);
  
  return {
    events,
    running,
    runAnalysis,
    clearEvents,
  };
}